/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Stock React Query Hooks — Phase 4 + Phase 5 + Phase 6 (Movement Hub)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDashboardStats,
  getStockMatrix,
  getStockLevel,
  getProductStockLevels,
  getAvailableProductsAtLocation,
  applyAdjustment,
  applyBatchAdjustment,
  getMovementHistory,
  getMovementSummary,
  getLowStockAlerts,
  getLowStockSummary,
  getVariantsAtLocation,
  getMigrationStatus,
  migrateToVariants,
  reclassifyPreview,
  reclassifyStock,
} from '../services/stock.service';
import type { LocationProduct } from '../services/stock.service';
import type {
  StockAdjustmentRequest,
  StockAdjustmentResult,
  StockAdjustmentBatchRequest,
  StockAdjustmentBatchResult,
  StockMovementListItem,
  StockMovementSummary,
  StockMovementHistoryParams,
  StockByLocationItem,
  LowStockAlert,
  LowStockSummary,
  LowStockAlertParams,
  VariantStockItem,
  MigrationStatus,
  MigrateToVariantsRequest,
  MigrateToVariantsResult,
  ReclassifyStockRequest,
  ReclassifyPreviewResponse,
} from '../types/stock';
import type { PagedResult } from '../types/product';
import type { ApiError } from '@/services/api/types';

export const stockKeys = {
  all: ['venta', 'stock'] as const,
  dashboard: () => [...stockKeys.all, 'dashboard'] as const,
  matrix: () => [...stockKeys.all, 'matrix'] as const,
  matrixFiltered: (params: { categoryId?: string; search?: string; page: number; size: number }) =>
    [...stockKeys.matrix(), params] as const,
  level: (productId: string, locationId: string) =>
    [...stockKeys.all, 'level', productId, locationId] as const,
  productLevels: (productId: string) =>
    [...stockKeys.all, 'productLevels', productId] as const,
  movementList: (params: StockMovementHistoryParams) =>
    [...stockKeys.all, 'movements', params] as const,
  movementSummary: () => [...stockKeys.all, 'movementSummary'] as const,
  alerts: (params: LowStockAlertParams) => [...stockKeys.all, 'alerts', params] as const,
  alertsSummary: () => [...stockKeys.all, 'alertsSummary'] as const,
  byLocation: (locationId: string) => [...stockKeys.all, 'byLocation', locationId] as const,
  variantsAtLocation: (productId: string, locationId: string) =>
    [...stockKeys.all, 'variantsAtLocation', productId, locationId] as const,
  migrationStatus: (productId: string, locationId: string) =>
    [...stockKeys.all, 'migrationStatus', productId, locationId] as const,
};

export function useStockDashboard(locationId?: string) {
  return useQuery({
    queryKey: [...stockKeys.dashboard(), locationId ?? 'all'],
    queryFn: () => getDashboardStats(locationId),
    staleTime: 30_000,
  });
}

export function useStockMatrix(categoryId?: string, search?: string, page = 0, size = 20) {
  return useQuery({
    queryKey: stockKeys.matrixFiltered({ categoryId, search, page, size }),
    queryFn: () => getStockMatrix({ categoryId, search, page, size }),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch current stock level for a specific product at a specific location.
 * Enabled only when both productId and locationId are provided.
 */
export function useStockLevel(productId?: string, locationId?: string) {
  return useQuery<number, ApiError>({
    queryKey: stockKeys.level(productId ?? '', locationId ?? ''),
    queryFn: () => getStockLevel(productId!, locationId!),
    enabled: !!productId && !!locationId,
    staleTime: 15_000,
  });
}

/**
 * Fetch available products (stock > 0) at a specific location.
 * Used in transfer form to filter product dropdown by source location.
 */
export function useAvailableProductsAtLocation(locationId?: string) {
  return useQuery<LocationProduct[], ApiError>({
    queryKey: stockKeys.byLocation(locationId ?? ''),
    queryFn: () => getAvailableProductsAtLocation(locationId!),
    enabled: !!locationId,
    staleTime: 15_000,
  });
}

/**
 * Fetch all stock levels for a product across all locations.
 * Used in the adjustment modal right panel.
 */
export function useProductStockLevels(productId?: string) {
  return useQuery<StockByLocationItem[], ApiError>({
    queryKey: stockKeys.productLevels(productId ?? ''),
    queryFn: () => getProductStockLevels(productId!),
    enabled: !!productId,
    staleTime: 15_000,
  });
}

/**
 * Fetch variant-level stock at a specific location for a product.
 * Used when a variant-product is selected in adjustment modal or transfer form.
 */
export function useVariantsAtLocation(productId?: string, locationId?: string) {
  return useQuery<VariantStockItem[], ApiError>({
    queryKey: stockKeys.variantsAtLocation(productId ?? '', locationId ?? ''),
    queryFn: () => getVariantsAtLocation(productId!, locationId!),
    enabled: !!productId && !!locationId,
    staleTime: 15_000,
  });
}

/**
 * Fetch paginated movement history with optional filters.
 */
export function useMovementHistory(params: StockMovementHistoryParams) {
  return useQuery<PagedResult<StockMovementListItem>, ApiError>({
    queryKey: stockKeys.movementList(params),
    queryFn: () => getMovementHistory(params),
    staleTime: 15_000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch movement summary (stats + daily chart data).
 */
export function useMovementSummary() {
  return useQuery<StockMovementSummary, ApiError>({
    queryKey: stockKeys.movementSummary(),
    queryFn: getMovementSummary,
    staleTime: 30_000,
  });
}

/**
 * Apply a stock adjustment mutation.
 * Invalidates all stock queries on success.
 */
export function useApplyAdjustment() {
  const queryClient = useQueryClient();

  return useMutation<StockAdjustmentResult, ApiError, StockAdjustmentRequest>({
    mutationFn: applyAdjustment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

/**
 * Apply a multi-product stock adjustment.
 *
 * One request rather than a loop, so a failure part-way through does not leave
 * the count half-applied.
 */
export function useApplyBatchAdjustment() {
  const queryClient = useQueryClient();

  return useMutation<StockAdjustmentBatchResult, ApiError, StockAdjustmentBatchRequest>({
    mutationFn: applyBatchAdjustment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

/**
 * Fetch paginated low stock alerts with optional filters.
 */
export function useLowStockAlerts(params: LowStockAlertParams) {
  return useQuery<PagedResult<LowStockAlert>, ApiError>({
    queryKey: stockKeys.alerts(params),
    queryFn: () => getLowStockAlerts(params),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch low stock alert summary counts.
 */
export function useLowStockSummary() {
  return useQuery<LowStockSummary, ApiError>({
    queryKey: stockKeys.alertsSummary(),
    queryFn: getLowStockSummary,
    staleTime: 30_000,
  });
}

/**
 * Check whether a variant product has unmigrated parent stock at a location.
 */
export function useMigrationStatus(productId?: string, locationId?: string) {
  return useQuery<MigrationStatus, ApiError>({
    queryKey: stockKeys.migrationStatus(productId ?? '', locationId ?? ''),
    queryFn: () => getMigrationStatus(productId!, locationId!),
    enabled: !!productId && !!locationId,
    staleTime: 15_000,
  });
}

/**
 * Migrate parent stock to individual variant stock levels.
 * Invalidates all stock queries on success.
 */
export function useMigrateToVariants() {
  const queryClient = useQueryClient();

  return useMutation<MigrateToVariantsResult, ApiError, MigrateToVariantsRequest>({
    mutationFn: migrateToVariants,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

/**
 * Preview a stock reclassification (shows before/after for both statuses).
 */
export function useReclassifyPreview() {
  return useMutation<ReclassifyPreviewResponse, ApiError, ReclassifyStockRequest>({
    mutationFn: reclassifyPreview,
  });
}

/**
 * Apply a stock reclassification (move stock between quality statuses).
 * Invalidates all stock queries on success.
 */
export function useReclassifyStock() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, ReclassifyStockRequest>({
    mutationFn: reclassifyStock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}
